import type { AppState } from 'src/reducers';
import { createSelector, type Selector } from 'reselect';
import { flattenAndLabelGroup } from 'src/components/StandardCardView/SortAndGroup';
import type { CardViewDataInputs, CardViewOptionalInputs } from 'src/components/StandardCardView/UIData.types';
import type { TreePivot, WorklistInfo } from 'src/worker/pivotWorker.types';
import type { SummaryViewSlice } from './SummaryView.slice';
import type { SubheaderSlice } from 'src/components/Subheader/Subheader.slice';
import { externalGridSearchFields } from 'src/utils/Domain/Constants';
import { toSummaries } from 'src/utils/Pivot/RollUp';
import type { GetSummaryOutput } from 'src/pages/Hindsighting/StyleColorReview/CollectionView/CollectionView.selectors';
import { filterData } from 'src/utils/Pivot/Filter';
import processDataForCardView from 'src/components/StandardCardView/CardViewDataProcessor';
import { getGroupBySelectedOptionProperty } from 'src/utils/Pivot/Sort';
import { getDataFromCache } from 'src/services/pivotServiceCache';
import { markSelectedOnGroups } from 'src/components/StandardCardView/StandardCardView.utils';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';

const getTreeData: Selector<AppState, TreePivot> = (state) => {
  return getDataFromCache(state.pages.hindsighting.styleColorReview.summaryView)?.tree || [];
};
const getRollup: Selector<AppState, TenantConfigViewData> = (state) => {
  return state.pages.hindsighting.styleColorReview.summaryView.calcViewDefn;
};
const getWorklist: Selector<AppState, WorklistInfo[]> = (state) => {
  return state.pages.hindsighting.styleColorReview.sharedData.worklist;
};
const getViewSlice: Selector<AppState, SummaryViewSlice> = (state) => {
  return state.pages.hindsighting.styleColorReview.summaryView;
};
const getSubheader: Selector<AppState, SubheaderSlice> = (state) => {
  return state.subheader;
};
const getSearchIndexes: Selector<AppState, string[] | undefined> = (state) => {
  return state.pages.hindsighting.styleColorReview.summaryView.viewDefn.searchIndexes;
};

export const getProcessedData = createSelector(
  getTreeData,
  getViewSlice,
  getSubheader,
  getWorklist,
  (treeData, viewSlice, subheader, worklist) => {
    if (treeData && viewSlice && subheader) {
      const dataInputs: CardViewDataInputs = {
        styles: treeData,
        subheader,
        defns: {
          view: viewSlice.viewDefn || {},
          rollup: viewSlice.calcViewDefn || {},
        },
      };
      const optionalInputs: CardViewOptionalInputs = { worklist };
      const groups = processDataForCardView(dataInputs, optionalInputs);

      const selectionDataIndex = viewSlice.unmodifiedViewDefn?.main?.selectionDataIndex ?? undefined;

      return markSelectedOnGroups(groups, viewSlice.selectedIds ?? [], selectionDataIndex);
    }
    return [];
  },
);

export const getSummary = createSelector(
  getTreeData,
  getViewSlice,
  getRollup,
  getSubheader,
  getSearchIndexes,
  (treeData, viewSlice, rollupConfig, subheader, searchIndexes) => {
    if (!treeData) {
      return [] as GetSummaryOutput[];
    }
    const views = rollupConfig.view;
    const groupDataIndex = getGroupBySelectedOptionProperty(subheader.groupBy, 'dataIndex');
    const groupDimension = getGroupBySelectedOptionProperty(subheader.groupBy, 'dimension');
    const flat = flattenAndLabelGroup({ items: treeData, groupDataIndex, groupDimension });
    const searchFields = searchIndexes ?? externalGridSearchFields;

    if (flat.length === 0 || !Array.isArray(views) || views.length === 0) {
      return [] as GetSummaryOutput[];
    }
    const filteredFlat = filterData(
      flat,
      subheader.search,
      searchFields,
      subheader.flowStatus,
      subheader.pareDown && subheader.pareDown.selections,
    );
    return toSummaries(filteredFlat, views, viewSlice.identityPropsConfig.id);
  },
);

export const getFilteredFlatData = createSelector(
  getTreeData,
  getSubheader,
  getSearchIndexes,
  (treeData, subheader, searchIndexes) => {
    const groupDataIndex = getGroupBySelectedOptionProperty(subheader.groupBy, 'dataIndex');
    const groupDimension = getGroupBySelectedOptionProperty(subheader.groupBy, 'dimension');
    const flat = flattenAndLabelGroup({ items: treeData, groupDataIndex, groupDimension });
    const searchFields = searchIndexes ?? externalGridSearchFields;
    const pareDown = subheader.pareDown && subheader.pareDown.selections;
    const filteredFlat = filterData(flat, subheader.search, searchFields, subheader.flowStatus, pareDown);
    return filteredFlat;
  },
);
